import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Card, CardContent, Grid, Typography, useTheme,
  TextField, Chip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress
} from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config";

const Subscriptions = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [tiers, setTiers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal edit tier
  const [editTier, setEditTier] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const authToken = sessionStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/admin/subscriptions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authToken }),
      });
      const data = await res.json();
      if (data.success) {
        setTiers(data.tiers || []);
        setDrivers(data.drivers || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Erreur de chargement des abonnements");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const handleUpdateTier = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const authToken = sessionStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/admin/subscriptions/update-tier`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          authToken,
          id: editTier.id,
          monthly_price: editTier.monthly_price,
          quarterly_price: editTier.quarterly_price,
          yearly_price: editTier.yearly_price
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Tarif de la formule mis à jour avec succès !");
        setEditTier(null);
        fetchSubscriptions();
      } else {
        toast.error(data.message || "Erreur de mise à jour");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erreur réseau");
    } finally {
      setSaving(false);
    }
  };

  const handleExtendDriver = async (driverId) => {
    try {
      const authToken = sessionStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/admin/subscriptions/extend-driver`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authToken, driver_id: driverId, months: 1 })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Abonnement du chauffeur prolongé de 1 mois !");
        fetchSubscriptions();
      } else {
        toast.error(data.message || "Erreur lors de la prolongation");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erreur réseau");
    }
  };

  if (loading && tiers.length === 0) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header title="GESTION DES ABONNEMENTS CHAUFFEURS" subtitle="Tarifs des formules LinkerVTC et suivi de validité des comptes" />

      {/* FORMULES / TIERS */}
      <Typography variant="h4" fontWeight="600" color={colors.grey[100]} mt="25px" mb="15px">
        Formules & Tarifs Chauffeurs
      </Typography>

      <Grid container spacing={3}>
        {tiers.map((tier) => (
          <Grid item xs={12} sm={6} md={2.4} key={tier.id}>
            <Card sx={{ backgroundColor: colors.primary[400], height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb="10px">
                  <Typography variant="h5" fontWeight="bold" color={colors.greenAccent[400]}>
                    {tier.name}
                  </Typography>
                  <IconButton size="small" onClick={() => setEditTier(tier)} sx={{ color: colors.grey[100] }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Typography variant="body2" color={colors.grey[300]}>1 Mois</Typography>
                <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb="10px">
                  {tier.monthly_price} DZD
                </Typography>

                <Typography variant="body2" color={colors.grey[300]}>Trimestre (3M)</Typography>
                <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb="10px">
                  {tier.quarterly_price} DZD
                </Typography>

                <Typography variant="body2" color={colors.grey[300]}>Annuel (1 An)</Typography>
                <Typography variant="h6" fontWeight="bold" color={colors.grey[100]}>
                  {tier.yearly_price} DZD
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* CHAUFFEURS & STATUT D'EXPIRATION */}
      <Typography variant="h4" fontWeight="600" color={colors.grey[100]} mt="40px" mb="15px">
        Statuts Récent des Abonnements Chauffeurs
      </Typography>

      <Box backgroundColor={colors.primary[400]} borderRadius="4px" p="20px" overflow="auto">
        {drivers.length === 0 ? (
          <Typography color={colors.grey[300]}>Aucun chauffeur trouvé.</Typography>
        ) : (
          drivers.map((d) => {
            const exp = d.subscription ? new Date(d.subscription) : null;
            const isActive = exp && exp > new Date();
            return (
              <Box
                key={d.id}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                borderBottom={`1px solid ${colors.primary[500]}`}
                p="12px 0"
                flexWrap="wrap"
                gap="10px"
              >
                <Box>
                  <Typography fontWeight="bold" color={colors.grey[100]}>
                    {d.name} {d.fname}
                  </Typography>
                  <Typography variant="caption" color={colors.grey[300]}>
                    Tél : {d.phone} • ID : {d.id}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap="15px">
                  <Chip
                    label={isActive ? `Actif jusqu'au ${exp.toLocaleDateString('fr-FR')}` : "Expiré / Inactif"}
                    size="small"
                    sx={{
                      backgroundColor: isActive ? colors.greenAccent[700] : "#d32f2f",
                      color: "#fff",
                      fontWeight: "bold"
                    }}
                  />

                  <Button
                    onClick={() => handleExtendDriver(d.id)}
                    size="small"
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon />}
                    sx={{ backgroundColor: colors.blueAccent[600], fontSize: "11px" }}
                  >
                    +1 Mois Gratuit
                  </Button>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* MODAL ÉDITION FORMULE */}
      {editTier && (
        <Dialog open={Boolean(editTier)} onClose={() => setEditTier(null)} maxWidth="xs" fullWidth>
          <form onSubmit={handleUpdateTier}>
            <DialogTitle sx={{ backgroundColor: colors.primary[400], color: colors.grey[100] }}>
              Modifier Tarifs : {editTier.name}
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: colors.primary[400], pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Prix Mensuel (DZD)"
                type="number"
                fullWidth
                value={editTier.monthly_price}
                onChange={(e) => setEditTier({ ...editTier, monthly_price: e.target.value })}
                sx={{ mt: 1 }}
              />
              <TextField
                label="Prix Trimestriel (DZD)"
                type="number"
                fullWidth
                value={editTier.quarterly_price}
                onChange={(e) => setEditTier({ ...editTier, quarterly_price: e.target.value })}
              />
              <TextField
                label="Prix Annuel (DZD)"
                type="number"
                fullWidth
                value={editTier.yearly_price}
                onChange={(e) => setEditTier({ ...editTier, yearly_price: e.target.value })}
              />
            </DialogContent>
            <DialogActions sx={{ backgroundColor: colors.primary[400], p: 2 }}>
              <Button onClick={() => setEditTier(null)} color="inherit">Annuler</Button>
              <Button type="submit" variant="contained" disabled={saving} sx={{ backgroundColor: colors.greenAccent[600] }}>
                {saving ? "Sauvegarde..." : "Enregistrer"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}
    </Box>
  );
};

export default Subscriptions;
